import cn from "mcn";
import { nanoid } from "nanoid";
import { useMemo, useRef } from "react";
import type React from "react";
import { forwardRef } from "react";

const variations = {
  unchecked: {
    enabled: cn(
      "bg-outline border-outline hover:bg-outline-variant hover:border-outline-variant peer-focus:bg-outline-variant peer-focus:border-primary",
    ),
    disabled: cn("bg-neutral-disabled border-neutral-on-disabled"),
    hovering: cn("bg-outline-variant border-outline-variant"),
    focusing: cn("bg-outline-variant border-primary"),
  },
  checked: {
    enabled: cn("bg-primary border-primary hover:bg-primary-hover hover:border-primary-hover"),
    disabled: cn("bg-primary-disabled border-primary-on-disabled"),
    hovering: cn("bg-primary-hover border-primary-hover"),
    focusing: cn("bg-primary-hover border-primary"),
  },
};

export const Toggle = forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input"> & {
    label?: string | React.ReactNode;
  }
>(function Toggle(props, ref) {
  const { id, disabled, checked, label, className, onChange } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checkboxId = useMemo(() => id ?? nanoid(), [id]);

  const variation = checked ? variations.checked : variations.unchecked;

  const style = disabled ? variation.disabled : variation.enabled;

  const checkedClassName = cn(
    "peer-checked:flex items-center justify-center absolute top-[2px] w-[18px] h-[18px] rounded-full border-0 bg-white pointer-events-none peer-focus:border-1 peer-focus:border-primary",
    [disabled, "bg-neutral-on-disabled!"],
    [checked, "right-[2px]", "left-[2px]"],
    [disabled && checked, "bg-primary-on-disabled!"],
  );

  const combinedRef = (node: HTMLInputElement) => {
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
    inputRef.current = node;
  };

  return (
    <div className={cn("flex items-center", className)}>
      <div
        className={cn(
          "relative rounded-full border-[1px] focus-within:border-primary overflow-hidden w-[40px] h-[24px]",
          style,
          [checked, "focus-within:border-primary focus-within:bg-primary-hover", "focus-within:bg-outline-variant"],
        )}
      >
        <input
          ref={combinedRef}
          type="checkbox"
          {...props}
          onChange={(e) => {
            onChange?.(e);
            if (inputRef.current) {
              inputRef.current.blur();
            }
          }}
          id={checkboxId}
          className={cn("peer absolute opacity-0 z-0 w-full h-full", [!disabled, "cursor-pointer"])}
        />
        <div className={checkedClassName} />
      </div>
      {label && (
        <label htmlFor={checkboxId} className="text-md text-foreground ml-2 cursor-pointer">
          {label}
        </label>
      )}
    </div>
  );
});
